import { useEffect, useRef, useState } from "react";

// Three story moments — large imagery, minimal text. Reveal on scroll.
const STORIES = [
  {
    eyebrow: "01 — The hands",
    title: "Stitched, not shipped.",
    body: "Every quilt, sign, and stoneware mug is finished by one of three makers — never a line, never a machine.",
    image:
      "https://readdy.ai/api/search-image?query=Close%20up%20of%20a%20woman%20artisan%20hands%20stitching%20a%20cream%20linen%20quilt%20by%20a%20bright%20farmhouse%20window%20warm%20natural%20morning%20light%20muted%20cream%20and%20honey%20palette%20editorial%20documentary%20photography%20soft%20shadows%20simple%20uncluttered%20background%20quiet%20focused%20mood&width=1200&height=1500&seq=story-hands-01&orientation=portrait",
    align: "right",
  },
  {
    eyebrow: "02 — The material",
    title: "Linen. Oak. Beeswax.",
    body: "Sourced within a day’s drive. Honest fibers and woods that soften and patina the longer you live with them.",
    image:
      "https://readdy.ai/api/search-image?query=A%20still%20life%20of%20natural%20materials%20folded%20cream%20linen%20a%20cut%20oak%20board%20and%20a%20beeswax%20pillar%20candle%20arranged%20on%20a%20worn%20oak%20farmhouse%20table%20warm%20natural%20morning%20light%20muted%20cream%20and%20honey%20palette%20editorial%20documentary%20photography%20soft%20shadows%20simple%20uncluttered%20composition%20quiet%20mood&width=1200&height=1500&seq=story-material-02&orientation=portrait",
    align: "left",
  },
  {
    eyebrow: "03 — The hearth",
    title: "Goods that gather.",
    body: "Pieces meant to outlast trends — passed from a bedside to a daughter’s, from a Sunday table to the next.",
    image:
      "https://readdy.ai/api/search-image?query=A%20sunlit%20farmhouse%20dining%20table%20set%20with%20handmade%20stoneware%20plates%20a%20cream%20linen%20runner%20a%20small%20vase%20of%20dried%20wheat%20warm%20natural%20morning%20light%20muted%20cream%20honey%20and%20sage%20palette%20editorial%20documentary%20photography%20soft%20shadows%20simple%20uncluttered%20composition%20cozy%20heirloom%20mood&width=1200&height=1500&seq=story-hearth-03&orientation=portrait",
    align: "right",
  },
];

function StoryRow({
  story,
  index,
}: {
  story: (typeof STORIES)[number];
  index: number;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const reverse = story.align === "left";

  return (
    <div
      ref={ref}
      className={`grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10 items-center transition-all duration-1000 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
    >
      <div
        className={`lg:col-span-7 ${reverse ? "lg:order-2" : "lg:order-1"}`}
      >
        <div className="relative w-full aspect-[4/5] md:aspect-[5/4] overflow-hidden rounded-lg bg-background-100 group">
          <img
            src={story.image}
            alt={story.title}
            title={`${story.title} — Hearth & Heirloom story`}
            className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1800ms] ease-out group-hover:scale-[1.04]"
          />
          <span className="absolute top-4 left-4 md:top-6 md:left-6 px-3 py-1 rounded-full bg-background-50/85 text-foreground-950 text-[10px] tracking-[0.3em] uppercase">
            Chapter {String(index + 1).padStart(2, "0")}
          </span>
        </div>
      </div>
      <div
        className={`lg:col-span-5 px-1 md:px-2 ${
          reverse ? "lg:order-1" : "lg:order-2"
        }`}
      >
        <p className="text-xs tracking-[0.3em] uppercase text-foreground-600 mb-3">
          {story.eyebrow}
        </p>
        <h3 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-950 leading-tight">
          {story.title}
        </h3>
        <p className="mt-4 md:mt-5 text-sm md:text-base text-foreground-700 max-w-md leading-relaxed">
          {story.body}
        </p>
      </div>
    </div>
  );
}

export default function BrandStory() {
  return (
    <section className="px-4 md:px-10 lg:px-14 py-20 md:py-28 bg-background-100/60">
      <div className="max-w-3xl mb-16 md:mb-20">
        <p className="text-xs tracking-[0.3em] uppercase text-foreground-600 mb-3">
          Our story
        </p>
        <h2 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight">
          Three makers. One quiet table.
        </h2>
      </div>
      <div className="space-y-20 md:space-y-28">
        {STORIES.map((s, i) => (
          <StoryRow key={s.title} story={s} index={i} />
        ))}
      </div>
    </section>
  );
}